import { inject, Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import _cloneDeep from 'lodash-es/cloneDeep';
import { ShowIfHttpError } from '@certemy/common';
import { SmartTable } from '@certemy/ui-components';
import { buildQueryParams, createQueryParam } from 'utils/helpers';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import _omitBy from 'lodash-es/omitBy';
import _isNil from 'lodash-es/isNil';
import { PsvService } from 'services/psv/psv.service';
import {
  OrganizationSubscriptionData,
  SubscriptionDocumentsCustomFilter,
  SubscriptionsListCustomFilter,
  SubscriptionsListResponse,
} from 'features/subscriptions/subscriptions.interface';
import {
  DEFAULT_SUBSCRIPTIONS_LISTS_SMART_FILTERS,
  DUE_DATE_RANGE_DEFAULT_VALUE,
} from 'features/subscriptions/subscriptions.constants';
import { SubscriptionReportsResponse } from 'features/subscriptions/subscription-reports/subscription-reports.interface';
import { SubscriptionDocumentsResponse } from 'features/subscriptions/subscription-documents/subscription-documents.interface';
import { SubscriptionDifferentialResponse } from 'features/subscriptions/subscription-differential/subscription-differential.interface';

@Injectable()
export class SubscriptionsResolverService  {
  constructor(private subscriptionsService: SubscriptionsService) {}
  resolve(): Observable<SubscriptionsListResponse> {
    return this.subscriptionsService.getSubscriptions({
      filters: { ...this.subscriptionsService.getFilters() },
    });
  }
}

export const subscriptionsResolver: ResolveFn<Observable<SubscriptionsListResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(SubscriptionsResolverService).resolve();
};

@Injectable()
export class SubscriptionReportsResolverService  {
  constructor(private subscriptionsService: SubscriptionsService) {}
  resolve(route: ActivatedRouteSnapshot): Observable<SubscriptionReportsResponse> {
    const key = route.paramMap.get('key');
    return this.subscriptionsService.getReports(key, {});
  }
}

export const subscriptionReportsResolver: ResolveFn<Observable<SubscriptionReportsResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(SubscriptionReportsResolverService).resolve(route);
};

@Injectable()
export class SubscriptionDocumentsResolverService {
  constructor(
    private subscriptionsService: SubscriptionsService,
    private subscriptionDocumentsService: SubscriptionDocumentsService,
  ) {}
  resolve(route: ActivatedRouteSnapshot): Observable<SubscriptionDocumentsResponse> {
    const key = route.paramMap.get('key');
    return this.subscriptionsService.getDocuments(key, {
      filters: { ...this.subscriptionDocumentsService.getFilters() },
    });
  }
}

export const subscriptionDocumentsResolver: ResolveFn<Observable<SubscriptionDocumentsResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(SubscriptionDocumentsResolverService).resolve(route);
};

@Injectable()
export class SubscriptionDocumentsService {
  smartFiltersValues = _cloneDeep(DUE_DATE_RANGE_DEFAULT_VALUE);

  resetSavedSmartFiltersValues(): void {
    this.smartFiltersValues = _cloneDeep(DUE_DATE_RANGE_DEFAULT_VALUE);
  }

  getFilters(): SubscriptionDocumentsCustomFilter {
    const { date } = this.smartFiltersValues;
    return _omitBy(
      {
        startDate: date.from,
        endDate: date.to,
      },
      _isNil,
    ) as SubscriptionDocumentsCustomFilter;
  }
}

@Injectable()
export class SubscriptionsService {
  smartFiltersValues = _cloneDeep(DEFAULT_SUBSCRIPTIONS_LISTS_SMART_FILTERS);

  constructor(private http: HttpService, private psvService: PsvService) {}

  resetSavedSmartFiltersValues(): void {
    this.smartFiltersValues = _cloneDeep(DEFAULT_SUBSCRIPTIONS_LISTS_SMART_FILTERS);
  }

  getFilters(): SubscriptionsListCustomFilter {
    const { statusId } = this.smartFiltersValues;
    return _omitBy(
      {
        statusId,
      },
      _isNil,
    ) as SubscriptionsListCustomFilter;
  }

  @ShowIfHttpError('Cannot receive subscriptions list.')
  getSubscriptions({
    search = '',
    orderId = 'publishDate',
    orderType = SmartTable.Order.DESC,
    filters = null,
    offset = 0,
    limit = 10,
  }): Observable<SubscriptionsListResponse> {
    const params = [
      createQueryParam('search', search),
      createQueryParam('filters', filters),
      createQueryParam('orderId', orderId),
      createQueryParam('orderType', orderType),
      createQueryParam('offset', offset),
      createQueryParam('limit', limit),
    ];
    const queryParams = buildQueryParams(params);

    return this.http.get(`/organization-license-monitoring${queryParams}`);
  }

  @ShowIfHttpError('Cannot receive reports')
  getReports(
    key: string,
    { page = 1, limit = 10, sortBy = 'publishDate:DESC' },
  ): Observable<SubscriptionReportsResponse> {
    const params = [
      createQueryParam('sortBy', sortBy),
      createQueryParam('page', page),
      createQueryParam('limit', limit),
    ];
    const queryParams = buildQueryParams(params);
    return this.psvService.get(
      `/v1/license-requirement-monitoring/organization-subscription-reports/${key}/${queryParams}`,
    );
  }

  @ShowIfHttpError('Cannot receive documents')
  getDocuments(
    key: string,
    { page = 1, limit = 10, sortBy = 'documentSourceDate:DESC', filters = null },
  ): Observable<SubscriptionDocumentsResponse> {
    const formattedFilters = Object.entries(filters).map(([key, value]) => (value ? `&filter.${key}=${value}` : ''));
    const params = [
      createQueryParam('sortBy', sortBy),
      createQueryParam('page', page),
      createQueryParam('limit', limit),
    ];
    const queryParams = buildQueryParams(params);
    return this.psvService.get(
      `/v1/license-requirement-monitoring/organization-subscription-documents/${key}${queryParams}${formattedFilters}`,
    );
  }

  @ShowIfHttpError('Cannot receive documents diffs')
  getDocumentsDiffs(id: string): Observable<SubscriptionDifferentialResponse> {
    return this.psvService.get(`/v1/license-requirement-monitoring/document-diff/${id}`);
  }

  @ShowIfHttpError('Cannot receive documents diffs')
  compareDocuments(documentIds: {
    firstDocumentId: string;
    secondDocumentId: string;
  }): Observable<SubscriptionDifferentialResponse> {
    return this.psvService.post(`/v1/license-requirement-monitoring/compare-documents`, documentIds);
  }

  @ShowIfHttpError('Cannot receive title')
  getSubscriptionName(key: string): Observable<OrganizationSubscriptionData> {
    return this.http.get(`/organization-license-monitoring/organization-subscription/ingestionKey/${key}`);
  }
}
